import { cancelSubscriptionContract } from "../mutations/cancelSubscriptionContract.core.js";
import { isContractOwner } from "../utils/isContractOwner.js";
import { contractFulfillmentErrors } from "../getContractFulfillmentErrors.js";
import { updateSubscriptionContract } from "../mutations/updateSubscriptionContract.subscriptions.js";
import { SUBSCRIPTION_CONTRACT_META_KEY } from "../constants.js";
import { getSubscriptionContractDetails } from "./getSubscriptionContractDetails.js";
import { getSubscriptionContractOrderCount } from "../queries/getSubscriptionContractOrderCount.core.js";
import { getCustomerByIdentifier } from "../../customer/services/getCustomerByIdentifier.js";
import { getOrganizationCustomerByIdentifier } from "../../customer/services/getOrganizationCustomerByIdentifier.js";
import { normalizeCustomerForCart } from "../../customer/utils/normalizeCustomerForCart.js";
import { cartCustomerInputSchema } from "../../cart/schemas.js";
import { hydrateCart, changeCartItemPricing, placeCart, getOrderIntent, } from "../../cart/service.js";
import { createOrder } from "../../order/mutations/createOrder.orders.js";
import { ORDER_META_KEY, OrderType } from "../../order/constants.js";
import { SOURCE_VALUES } from "../constants.js";
export const onCancel = async ({ contractId, customerIdentifier, cancelReason, cancelSource, doNotSendEmailOnCancel = false, createEndingInvoice = false, cancelDespiteHavingUnfulfilledTerms, }, { client }) => {
    const contract = await getSubscriptionContractDetails({ contractId }, { client });
    if (!isContractOwner(contract, customerIdentifier))
        throw new Error(`Unauthorized: User is not permitted to cancel contract: ${contractId}`);
    const orderCount = await getSubscriptionContractOrderCount(contractId, client);
    const unfulfilledTerms = contractFulfillmentErrors({
        contract,
        terms: contract.terms,
        numOrders: orderCount,
        endingInvoice: contract.endingInvoice,
    });
    const notAllowedToCancel = !cancelDespiteHavingUnfulfilledTerms && unfulfilledTerms.length > 0;
    if (notAllowedToCancel) {
        return {
            message: "Cannot cancel contract due to unfulfilled terms",
            contractId,
            unfulfilledTerms: unfulfilledTerms.map((unfulfilledTerm) => unfulfilledTerm.message),
        };
    }
    const contractMetaFields = [
        ...(contract?.meta || []),
        ...addCancelReason(cancelReason),
        ...addCancelSource(cancelSource),
        ...addSkipEmailOnCancel(doNotSendEmailOnCancel),
    ];
    await addMetaFieldsToContract(contractId, client, contractMetaFields);
    const shouldCreateEndingInvoice = createEndingInvoice &&
        unfulfilledTerms.length > 0 &&
        unfulfilledTerms[0]?.endingInvoice;
    if (shouldCreateEndingInvoice) {
        const individualCustomer = await getCustomerByIdentifier({ customerIdentifier: contract.customerIdentifier }, { client });
        const customer = individualCustomer ??
            (await getOrganizationCustomerByIdentifier({ identifier: contract.customerIdentifier }, { client }));
        const endingInvoice = unfulfilledTerms[0]?.endingInvoice;
        const cart = await hydrateCart({
            customer: cartCustomerInputSchema.parse({
                ...normalizeCustomerForCart(customer),
                isGuest: false,
            }),
            items: [
                {
                    sku: endingInvoice?.sku || "",
                    quantity: 1,
                },
            ],
            meta: [{ key: "cartApiVersion", value: "BACK_OFFICE" }],
        }, client);
        if (endingInvoice?.alternativePrice ||
            endingInvoice?.alternativePrice === 0) {
            await changeCartItemPricing({
                cartId: cart.id,
                item: {
                    sku: endingInvoice.sku,
                    quantity: 1,
                    price: {
                        gross: endingInvoice.alternativePrice,
                        net: endingInvoice.alternativePrice,
                    },
                },
            }, client);
        }
        await placeCart({ cartId: cart.id }, client);
        const intent = await getOrderIntent({ cartId: cart.id }, client);
        if (!intent) {
            throw new Error(`Could not create ending invoice order for contractId: ${contractId}. Missing order intent for cartId: ${cart.id}`);
        }
        await createOrder({
            ...intent,
            cart: [
                ...intent.cart,
                {
                    name: contract.item.name,
                    sku: contract.item.sku,
                    subscriptionContractId: contractId,
                    quantity: 1,
                    price: {
                        gross: 0,
                        net: 0,
                        currency: "NOK",
                        tax: { name: "VAT", percent: 0 },
                    },
                },
            ],
            customer: {
                ...intent.customer,
                birthDate: intent.customer.birthDate
                    ? intent.customer.birthDate.split("T")[0]
                    : undefined,
                ...(intent.customer.companyName
                    ? { companyName: intent.customer.companyName }
                    : {}),
                ...(intent.customer.meta ? { meta: intent.customer.meta } : {}),
            },
            payment: [
                {
                    provider: "custom",
                    custom: {
                        properties: [{ property: "identifier", value: "invoice" }],
                    },
                },
            ],
            meta: [
                { key: ORDER_META_KEY.ORDER.ORDER_TYPE, value: OrderType.BOOK_CLUB },
                {
                    key: ORDER_META_KEY.ORDER.ORDER_SOURCE,
                    value: SOURCE_VALUES.CUSTOMER_SERVICE,
                },
                {
                    key: ORDER_META_KEY.ORDER.SUBSCRIPTION_CONTRACT_ID,
                    value: contractId,
                },
                ...intent.meta.filter(({ key }) => ![
                    ORDER_META_KEY.ORDER.ORDER_TYPE,
                    ORDER_META_KEY.ORDER.ORDER_SOURCE,
                    ORDER_META_KEY.ORDER.SUBSCRIPTION_CONTRACT_ID,
                ].includes(key)),
            ],
        }, { client });
    }
    await cancelSubscriptionContract({ id: contractId }, { client });
    return {
        message: `Cancelled contract ${contractId}`,
        contractId,
        unfulfilledTerms: unfulfilledTerms.map((unfulfilledTerm) => unfulfilledTerm.message),
    };
};
const addCancelReason = (cancelReason) => {
    if (!cancelReason)
        return [];
    return [
        {
            key: SUBSCRIPTION_CONTRACT_META_KEY.CANCEL_REASON,
            value: cancelReason,
        },
    ];
};
const addCancelSource = (cancelSource) => {
    if (!cancelSource)
        return [];
    return [
        {
            key: SUBSCRIPTION_CONTRACT_META_KEY.CANCEL_SOURCE,
            value: cancelSource,
        },
    ];
};
const addSkipEmailOnCancel = (doNotSendEmailOnCancel) => {
    if (!doNotSendEmailOnCancel)
        return [];
    return [
        {
            key: SUBSCRIPTION_CONTRACT_META_KEY.DO_NOT_SEND_CANCEL_EMAIL,
            value: "true",
        },
    ];
};
const addMetaFieldsToContract = async (contractId, client, metaFields) => {
    await updateSubscriptionContract({
        id: contractId,
        meta: metaFields,
    }, { client });
};
